import { useId } from "react";
import { cn } from "@/lib/cn";
import Tooltip from "@components/ui/tooltip";
import { StorydocSparklesIcon } from "./icons/sparkles";

type ThumbProps = {
  selected: boolean;
  filterId: string;
  clipId: string;
};

type ThumbColors = {
  bg: string;
  outerStroke: string;
  outerStrokeOpacity: number;
  innerStroke: string;
  innerFill: string;
};

const getColors = (selected: boolean): ThumbColors =>
  selected
    ? {
        bg: "#F2F0FF",
        outerStroke: "#610CFF",
        outerStrokeOpacity: 1,
        innerStroke: "#610CFF",
        innerFill: "#DDD6FF",
      }
    : {
        bg: "#F3F2F5",
        outerStroke: "#12033E",
        outerStrokeOpacity: 0.05,
        innerStroke: "#88819F",
        innerFill: "#E7E6EC",
      };

const ShadowFilter = ({ id }: { id: string }) => (
  <filter
    id={id}
    x="0"
    y="0"
    width="138.4"
    height="82.0942"
    filterUnits="userSpaceOnUse"
    colorInterpolationFilters="sRGB"
  >
    <feFlood floodOpacity="0" result="BackgroundImageFix" />
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
    <feOffset />
    <feGaussianBlur stdDeviation="0.5" />
    <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.04 0" />
    <feBlend mode="normal" in2="BackgroundImageFix" result="effect1" />
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
    <feOffset dy="1" />
    <feGaussianBlur stdDeviation="0.5" />
    <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.03 0" />
    <feBlend mode="normal" in2="effect1" result="effect2" />
    <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
    <feOffset dy="2" />
    <feGaussianBlur stdDeviation="0.5" />
    <feColorMatrix type="matrix" values="0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0 0.0392157 0 0 0 0.01 0" />
    <feBlend mode="normal" in2="effect2" result="effect3" />
    <feBlend mode="normal" in="SourceGraphic" in2="effect3" result="shape" />
  </filter>
);

const FrameBackground = ({ bg }: { bg: string }) => (
  <path
    d="M2 9.99999C2 5.58171 5.58172 2 10 2H128.4C132.818 2 136.4 5.58172 136.4 10V70.0941C136.4 74.5124 132.818 78.0941 128.4 78.0941H10C5.58172 78.0941 2 74.5124 2 70.0941V9.99999Z"
    fill={bg}
  />
);

const FrameBorder = ({ stroke, opacity }: { stroke: string; opacity: number }) => (
  <path
    d="M128.4 1.5C133.095 1.50021 136.9 5.30571 136.9 10V70.0938C136.9 74.788 133.095 78.5935 128.4 78.5938H10C5.30558 78.5937 1.5 74.7882 1.5 70.0938V10C1.5 5.30558 5.30558 1.5 10 1.5H128.4Z"
    stroke={stroke}
    strokeOpacity={opacity}
  />
);

const FrameClip = ({ id }: { id: string }) => (
  <clipPath id={id}>
    <path d="M2 9.99999C2 5.58171 5.58172 2 10 2H128.4C132.818 2 136.4 5.58172 136.4 10V70.0941C136.4 74.5124 132.818 78.0941 128.4 78.0941H10C5.58172 78.0941 2 74.5124 2 70.0941V9.99999Z" fill="white" />
  </clipPath>
);

// ── Thumb 1: Sparkles (AI generation) ────────────────────────────────────────
const Thumb1 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <FrameBackground bg={c.bg} />
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
        <g clipPath={`url(#${clipId})`}>
          <g transform="translate(57 27.1172)">
            <StorydocSparklesIcon size={24} className="tw-text-[color:var(--thumb-icon)]" style={{ color: c.innerStroke }} />
          </g>
        </g>
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <clipPath id={clipId}>
          <rect width="24" height="24" fill="white" transform="translate(57 27.1172)" />
        </clipPath>
      </defs>
    </>
  );
};

// ── Thumb 2: Header pill + 2 stroked lines (masks) ───────────────────────────
const Thumb2 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  const m1 = `${filterId}-m1`;
  const m2 = `${filterId}-m2`;
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <path
            d="M55.7354 24.562H83.4629C85.3959 24.562 86.9629 26.129 86.9629 28.062V34.8462C86.9628 36.7791 85.3958 38.3462 83.4629 38.3462H55.7354C53.8024 38.3462 52.2354 36.7791 52.2354 34.8462V28.062C52.2354 26.129 53.8024 24.562 55.7354 24.562Z"
            fill={c.innerFill}
            stroke={c.innerStroke}
          />
          <mask id={m1} fill="white">
            <path d="M35.5996 49.2019C35.5996 48.9258 35.8235 48.7019 36.0996 48.7019H103.1C103.376 48.7019 103.6 48.9258 103.6 49.2019C103.6 49.478 103.376 49.7019 103.1 49.7019H36.0996C35.8235 49.7019 35.5996 49.478 35.5996 49.2019Z" />
          </mask>
          <path
            d="M35.5996 49.2019C35.5996 48.9258 35.8235 48.7019 36.0996 48.7019H103.1C103.376 48.7019 103.6 48.9258 103.6 49.2019C103.6 49.478 103.376 49.7019 103.1 49.7019H36.0996C35.8235 49.7019 35.5996 49.478 35.5996 49.2019Z"
            fill={c.innerFill}
          />
          <path
            d="M36.0996 48.7019V49.9339H103.1V48.7019V47.4699H36.0996V48.7019ZM103.1 49.7019V48.4699H36.0996V49.7019V50.9339H103.1V49.7019ZM36.0996 49.7019V48.4699C36.5039 48.4699 36.8316 48.7976 36.8316 49.2019H35.5996H34.3676C34.3676 50.1585 35.1431 50.9339 36.0996 50.9339V49.7019ZM103.6 49.2019H102.368C102.368 48.7976 102.695 48.4699 103.1 48.4699V49.7019V50.9339C104.056 50.9339 104.832 50.1585 104.832 49.2019H103.6ZM103.1 48.7019V49.9339C102.695 49.9339 102.368 49.6062 102.368 49.2019H103.6H104.832C104.832 48.2453 104.056 47.4699 103.1 47.4699V48.7019ZM36.0996 48.7019V47.4699C35.1431 47.4699 34.3676 48.2453 34.3676 49.2019H35.5996H36.8316C36.8316 49.6062 36.5039 49.9339 36.0996 49.9339V48.7019Z"
            fill={c.innerStroke}
            mask={`url(#${m1})`}
          />
          <mask id={m2} fill="white">
            <path d="M45.0996 57.594C45.0996 57.3179 45.3235 57.094 45.5996 57.094H93.5996C93.8758 57.094 94.0996 57.3179 94.0996 57.594C94.0996 57.8701 93.8758 58.094 93.5996 58.094H45.5996C45.3235 58.094 45.0996 57.8701 45.0996 57.594Z" />
          </mask>
          <path
            d="M45.0996 57.594C45.0996 57.3179 45.3235 57.094 45.5996 57.094H93.5996C93.8758 57.094 94.0996 57.3179 94.0996 57.594C94.0996 57.8701 93.8758 58.094 93.5996 58.094H45.5996C45.3235 58.094 45.0996 57.8701 45.0996 57.594Z"
            fill={c.innerFill}
          />
          <path
            d="M45.5996 57.094V58.326H93.5996V57.094V55.862H45.5996V57.094ZM93.5996 58.094V56.862H45.5996V58.094V59.326H93.5996V58.094ZM45.5996 58.094V56.862C46.0039 56.862 46.3316 57.1897 46.3316 57.594H45.0996H43.8676C43.8676 58.5506 44.6431 59.326 45.5996 59.326V58.094ZM94.0996 57.594H92.8676C92.8676 57.1897 93.1953 56.862 93.5996 56.862V58.094V59.326C94.5562 59.326 95.3316 58.5506 95.3316 57.594H94.0996ZM93.5996 57.094V58.326C93.1953 58.326 92.8676 57.9983 92.8676 57.594H94.0996H95.3316C95.3316 56.6374 94.5562 55.862 93.5996 55.862V57.094ZM45.5996 57.094V55.862C44.6431 55.862 43.8676 56.6374 43.8676 57.594H45.0996H46.3316C46.3316 57.9983 46.0039 58.326 45.5996 58.326V57.094Z"
            fill={c.innerStroke}
            mask={`url(#${m2})`}
          />
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// ── Thumb 3: 3 text lines left + big rect right ──────────────────────────────
const Thumb3 = ({ selected, filterId }: ThumbProps) => {
  const c = getColors(selected);
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <FrameBackground bg={c.bg} />
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
        <rect x="19.8896" y="30.9795" width="30.9061" height="0.539062" rx="0.269531" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.539062" />
        <rect x="19.8896" y="38.8865" width="30.9061" height="0.539062" rx="0.269531" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.539062" />
        <rect x="19.8896" y="46.7913" width="23.0448" height="0.539062" rx="0.269531" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.539062" />
        <rect x="59.5176" y="20.7878" width="59.032" height="36.7344" rx="3.5" fill={c.innerFill} stroke={c.innerStroke} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
      </defs>
    </>
  );
};

// ── Thumb 4: 3 circles with 2 stacked lines each (Array) ─────────────────────
const Thumb4 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <rect x="23.6504" y="47.3792" width="24.5" height="0.5" rx="0.25" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.5" />
          <rect x="27.6504" y="53.3792" width="16.5" height="0.5" rx="0.25" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.5" />
          <path d="M35.8555 24.1543C40.3422 24.1543 43.9794 27.7915 43.9795 32.2783C43.9795 36.7652 40.3422 40.4023 35.8555 40.4023C31.3687 40.4023 27.7314 36.7652 27.7314 32.2783C27.7315 27.7915 31.3688 24.1543 35.8555 24.1543Z" fill={c.innerFill} stroke={c.innerStroke} />
          <rect x="56.6582" y="47.3792" width="24.5" height="0.5" rx="0.25" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.5" />
          <rect x="60.6943" y="53.3792" width="16.5" height="0.5" rx="0.25" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.5" />
          <path d="M68.8047 24.1543C73.2912 24.1544 76.9287 27.7916 76.9287 32.2783C76.9287 36.765 73.2912 40.4022 68.8047 40.4023C64.318 40.4023 60.6807 36.7651 60.6807 32.2783C60.6807 27.7915 64.318 24.1543 68.8047 24.1543Z" fill={c.innerFill} stroke={c.innerStroke} />
          <rect x="89.666" y="47.3792" width="24.5" height="0.5" rx="0.25" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.5" />
          <rect x="93.8018" y="53.3792" width="16.5" height="0.5" rx="0.25" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.5" />
          <path d="M101.821 24.1543C106.308 24.1544 109.945 27.7916 109.945 32.2783C109.945 36.765 106.308 40.4022 101.821 40.4023C97.3346 40.4023 93.6973 36.7651 93.6973 32.2783C93.6973 27.7915 97.3346 24.1543 101.821 24.1543Z" fill={c.innerFill} stroke={c.innerStroke} />
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// ── Thumb 5: 3 lines + 3 vertical stacked rects ──────────────────────────────
const Thumb5 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <rect x="20.6699" y="30.9795" width="30.9061" height="0.539062" rx="0.269531" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.539062" />
          <rect x="20.6699" y="38.8865" width="30.9061" height="0.539062" rx="0.269531" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.539062" />
          <rect x="20.6699" y="46.7913" width="23.0448" height="0.539062" rx="0.269531" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.539062" />
          <path d="M69.7979 40.0942H79.3857C79.8861 40.0942 80.3682 40.2963 80.7246 40.6587C81.0813 41.0214 81.2832 41.5152 81.2832 42.0317V56.6567C81.2832 57.1733 81.0813 57.667 80.7246 58.0298C80.3682 58.3922 79.8861 58.5942 79.3857 58.5942H69.7979C69.2974 58.5942 68.8154 58.3922 68.459 58.0298C68.1023 57.667 67.9004 57.1733 67.9004 56.6567V42.0317C67.9004 41.5152 68.1023 41.0214 68.459 40.6587C68.8154 40.2963 69.2974 40.0942 69.7979 40.0942Z" fill={c.innerFill} stroke={c.innerStroke} />
          <path d="M107.415 30.344H117.003C117.503 30.344 117.985 30.5461 118.342 30.9084C118.699 31.2712 118.9 31.765 118.9 32.2815V56.6565C118.9 57.173 118.699 57.6668 118.342 58.0295C117.985 58.3919 117.503 58.594 117.003 58.594H107.415C106.915 58.594 106.433 58.392 106.076 58.0295C105.719 57.6668 105.518 57.173 105.518 56.6565V32.2815C105.518 31.765 105.719 31.2712 106.076 30.9084C106.433 30.546 106.915 30.344 107.415 30.344Z" fill={c.innerFill} stroke={c.innerStroke} />
          <path d="M88.6064 20.5942H98.1943C98.6947 20.5942 99.1768 20.7963 99.5332 21.1587C99.8899 21.5214 100.092 22.0152 100.092 22.5317V56.6567C100.092 57.1733 99.8899 57.6671 99.5332 58.0298C99.1768 58.3922 98.6947 58.5942 98.1943 58.5942H88.6064C88.106 58.5942 87.624 58.3922 87.2676 58.0298C86.9108 57.6671 86.709 57.1733 86.709 56.6567V22.5317C86.709 22.0152 86.9108 21.5214 87.2676 21.1587C87.624 20.7963 88.106 20.5942 88.6064 20.5942Z" fill={c.innerFill} stroke={c.innerStroke} />
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// ── Carousel: one card with paging dots ──────────────────────────────────────
// Colours come from getColors like every other tile, so it themes with them:
// the card takes the inner fill and stroke, and the dots are drawn in the
// stroke colour on the frame's own background, which is what gives them their
// cut-out ring. The trailing dots are dimmed to read as "there is more".
const ThumbCarousel = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  const dot = (cx: number, opacity?: number) => (
    <circle
      cx={cx}
      cy="62.2581"
      r="3.20579"
      fill={c.innerStroke}
      stroke={c.bg}
      strokeWidth="2.41157"
      {...(opacity ? { opacity } : {})}
    />
  );

  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <path
            d="M45 15.7581H93C94.933 15.7581 96.5 17.3251 96.5 19.2581V46.2581C96.5 48.1911 94.933 49.7581 93 49.7581H45C43.067 49.7581 41.5 48.1911 41.5 46.2581V19.2581C41.5 17.3251 43.067 15.7581 45 15.7581Z"
            fill={c.innerFill}
            stroke={c.innerStroke}
          />
          {dot(61)}
          {dot(69, 0.53)}
          {dot(77, 0.53)}
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// ── Thumb 6: Tabs top (3 buttons) + content + 3 lines ────────────────────────
const Thumb6 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <rect x="72.3809" y="41.1226" width="31.16" height="19.1875" rx="3.5" fill={c.innerFill} stroke={c.innerStroke} />
          <rect x="33.5676" y="42.7355" width="30.8111" height="0.43006" rx="0.21503" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.43006" />
          <rect x="33.5676" y="50.4767" width="30.8111" height="0.43006" rx="0.21503" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.43006" />
          <rect x="33.5676" y="58.2177" width="23.0008" height="0.43006" rx="0.21503" fill={c.innerFill} stroke={c.innerStroke} strokeWidth="0.43006" />
          <path d="M102.687 19.8726H81.3916V31.6558H102.687C104.619 31.6558 106.186 30.0887 106.187 28.1558V23.3726C106.187 21.4396 104.62 19.8726 102.687 19.8726Z" stroke={c.innerStroke} />
          <rect x="56.5518" y="19.8726" width="24.7948" height="11.7831" fill={c.innerFill} stroke={c.innerStroke} />
          <path d="M35.1445 19.8726H56.4395V31.6558H35.1445C33.2116 31.6558 31.6446 30.0887 31.6445 28.1558V23.3726C31.6445 21.4396 33.2115 19.8726 35.1445 19.8726Z" stroke={c.innerStroke} />
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// ── Thumb 7: Timeline (line + 2 circles + 2 rects) ───────────────────────────
const Thumb7 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <line x1="69.5" y1="23.9653" x2="69.5" y2="61.4414" stroke={c.innerStroke} strokeLinecap="round" />
          <circle cx="69.4351" cy="21.9429" r="2.51098" fill={c.innerStroke} stroke={c.bg} strokeWidth="2.41157" />
          <circle cx="69.4351" cy="45.9429" r="2.51098" fill={c.innerStroke} stroke={c.bg} strokeWidth="2.41157" />
          <rect x="44.2666" y="16.3081" width="18.1429" height="16.2484" rx="3.5" fill={c.innerFill} stroke={c.innerStroke} />
          <rect x="76.4609" y="37.8687" width="18.1429" height="16.2484" rx="3.5" fill={c.innerFill} stroke={c.innerStroke} />
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// ── Thumb 8: Narrator (2 rects + square) ─────────────────────────────────────
const Thumb8 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <rect x="32.3398" y="35.2476" width="38.0152" height="8.16412" rx="3.5" fill={c.innerFill} stroke={c.innerStroke} />
          <rect opacity="0.5" x="32.3398" y="55.6646" width="38.0152" height="8.16412" rx="3.5" stroke={c.innerStroke} />
          <rect x="80.5352" y="26.6226" width="24.2452" height="24.2013" rx="3.5" fill={c.innerFill} stroke={c.innerStroke} />
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// ── Thumb 9: 3 stroked lines + big rounded rect ──────────────────────────────
const Thumb9 = ({ selected, filterId, clipId }: ThumbProps) => {
  const c = getColors(selected);
  const m1 = `${filterId}-m1`;
  const m2 = `${filterId}-m2`;
  const m3 = `${filterId}-m3`;
  return (
    <>
      <g filter={`url(#${filterId})`}>
        <g clipPath={`url(#${clipId})`}>
          <FrameBackground bg={c.bg} />
          <mask id={m1} fill="white">
            <path d="M47.8525 16.0401C47.8525 15.775 48.0674 15.5601 48.3325 15.5601H91.0525C91.3176 15.5601 91.5325 15.775 91.5325 16.0401C91.5325 16.3052 91.3176 16.5201 91.0525 16.5201H48.3325C48.0674 16.5201 47.8525 16.3052 47.8525 16.0401Z" />
          </mask>
          <path
            d="M47.8525 16.0401C47.8525 15.775 48.0674 15.5601 48.3325 15.5601H91.0525C91.3176 15.5601 91.5325 15.775 91.5325 16.0401C91.5325 16.3052 91.3176 16.5201 91.0525 16.5201H48.3325C48.0674 16.5201 47.8525 16.3052 47.8525 16.0401Z"
            fill={c.innerFill}
          />
          <path
            d="M48.3325 15.5601V16.5601H91.0525V15.5601V14.5601H48.3325V15.5601ZM91.0525 16.5201V15.5201H48.3325V16.5201V17.5201H91.0525V16.5201ZM48.3325 16.5201V15.5201C48.6197 15.5201 48.8525 15.7529 48.8525 16.0401H47.8525H46.8525C46.8525 16.8574 47.5152 17.5201 48.3325 17.5201V16.5201ZM91.5325 16.0401H90.5325C90.5325 15.7529 90.7654 15.5201 91.0525 15.5201V16.5201V17.5201C91.8699 17.5201 92.5325 16.8574 92.5325 16.0401H91.5325ZM91.0525 15.5601V16.5601C90.7654 16.5601 90.5325 16.3272 90.5325 16.0401H91.5325H92.5325C92.5325 15.2227 91.8699 14.5601 91.0525 14.5601V15.5601ZM48.3325 15.5601V14.5601C47.5152 14.5601 46.8525 15.2227 46.8525 16.0401H47.8525H48.8525C48.8525 16.3272 48.6197 16.5601 48.3325 16.5601V15.5601Z"
            fill={c.innerStroke}
            mask={`url(#${m1})`}
          />
          <mask id={m2} fill="white">
            <path d="M47.8525 23.24C47.8525 22.9749 48.0674 22.76 48.3325 22.76H91.0525C91.3176 22.76 91.5325 22.9749 91.5325 23.24C91.5325 23.5051 91.3176 23.72 91.0525 23.72H48.3325C48.0674 23.72 47.8525 23.5051 47.8525 23.24Z" />
          </mask>
          <path
            d="M47.8525 23.24C47.8525 22.9749 48.0674 22.76 48.3325 22.76H91.0525C91.3176 22.76 91.5325 22.9749 91.5325 23.24C91.5325 23.5051 91.3176 23.72 91.0525 23.72H48.3325C48.0674 23.72 47.8525 23.5051 47.8525 23.24Z"
            fill={c.innerFill}
          />
          <path
            d="M48.3325 22.76V23.76H91.0525V22.76V21.76H48.3325V22.76ZM91.0525 23.72V22.72H48.3325V23.72V24.72H91.0525V23.72ZM48.3325 23.72V22.72C48.6197 22.72 48.8525 22.9528 48.8525 23.24H47.8525H46.8525C46.8525 24.0574 47.5152 24.72 48.3325 24.72V23.72ZM91.5325 23.24H90.5325C90.5325 22.9528 90.7654 22.72 91.0525 22.72V23.72V24.72C91.8699 24.72 92.5325 24.0574 92.5325 23.24H91.5325ZM91.0525 22.76V23.76C90.7654 23.76 90.5325 23.5272 90.5325 23.24H91.5325H92.5325C92.5325 22.4226 91.8699 21.76 91.0525 21.76V22.76ZM48.3325 22.76V21.76C47.5152 21.76 46.8525 22.4226 46.8525 23.24H47.8525H48.8525C48.8525 23.5272 48.6197 23.76 48.3325 23.76V22.76Z"
            fill={c.innerStroke}
            mask={`url(#${m2})`}
          />
          <mask id={m3} fill="white">
            <path d="M47.7998 29.8045C47.7998 29.5394 48.0147 29.3245 48.2798 29.3245H78.5198C78.7849 29.3245 78.9998 29.5394 78.9998 29.8045C78.9998 30.0696 78.7849 30.2845 78.5198 30.2845H48.2798C48.0147 30.2845 47.7998 30.0696 47.7998 29.8045Z" />
          </mask>
          <path
            d="M47.7998 29.8045C47.7998 29.5394 48.0147 29.3245 48.2798 29.3245H78.5198C78.7849 29.3245 78.9998 29.5394 78.9998 29.8045C78.9998 30.0696 78.7849 30.2845 78.5198 30.2845H48.2798C48.0147 30.2845 47.7998 30.0696 47.7998 29.8045Z"
            fill={c.innerFill}
          />
          <path
            d="M48.2798 29.3245V30.3245H78.5198V29.3245V28.3245H48.2798V29.3245ZM78.5198 30.2845V29.2845H48.2798V30.2845V31.2845H78.5198V30.2845ZM48.2798 30.2845V29.2845C48.567 29.2845 48.7998 29.5173 48.7998 29.8045H47.7998H46.7998C46.7998 30.6218 47.4624 31.2845 48.2798 31.2845V30.2845ZM78.9998 29.8045H77.9998C77.9998 29.5173 78.2326 29.2845 78.5198 29.2845V30.2845V31.2845C79.3372 31.2845 79.9998 30.6218 79.9998 29.8045H78.9998ZM78.5198 29.3245V30.3245C78.2326 30.3245 77.9998 30.0917 77.9998 29.8045H78.9998H79.9998C79.9998 28.9871 79.3372 28.3245 78.5198 28.3245V29.3245ZM48.2798 29.3245V28.3245C47.4624 28.3245 46.7998 28.9871 46.7998 29.8045H47.7998H48.7998C48.7998 30.0917 48.567 30.3245 48.2798 30.3245V29.3245Z"
            fill={c.innerStroke}
            mask={`url(#${m3})`}
          />
          <path
            d="M51.7998 38.4644H87.3213C89.2543 38.4644 90.8213 40.0314 90.8213 41.9644V61.1655C90.8212 63.0984 89.2542 64.6655 87.3213 64.6655H51.7998C49.8669 64.6655 48.2999 63.0984 48.2998 61.1655V41.9644C48.2998 40.0314 49.8668 38.4644 51.7998 38.4644Z"
            fill={c.innerFill}
            stroke={c.innerStroke}
          />
        </g>
        <FrameBorder stroke={c.outerStroke} opacity={c.outerStrokeOpacity} />
      </g>
      <defs>
        <ShadowFilter id={filterId} />
        <FrameClip id={clipId} />
      </defs>
    </>
  );
};

// Picker order. The components keep their original names so the diff stays
// readable; the `thumb-N` ids below are positional, derived from this array,
// so moving a tile here moves its id -- see create-with-ai-layouts, which maps
// those ids to layouts and must move with them.
const THUMBS = [
  Thumb1,
  Thumb2,
  Thumb3,
  Thumb4,
  Thumb5,
  Thumb6,
  Thumb7,
  Thumb8,
  Thumb9,
  ThumbCarousel,
] as const;

export type LayoutThumbnailId = `thumb-${number}`;

/**
 * What each tile is, in the picker's order.
 *
 * These name the KIND of slide the drawing stands for, which is what a reader
 * needs to choose between ten grey wireframes. They double as the tiles'
 * accessible names -- "Select layout 7" told a screen-reader user nothing at
 * all about what they were selecting.
 */
export const LAYOUT_THUMBNAIL_LABELS: readonly string[] = [
  "Let AI decide",
  "Cover slide",
  "Side by side",
  "Array",
  "Data slide",
  "Tabs",
  "Timeline",
  "Narrator",
  "Long form",
  "Carousel",
];

export const layoutThumbnailLabel = (id: LayoutThumbnailId): string => {
  const index = Number(id.replace("thumb-", "")) - 1;
  return LAYOUT_THUMBNAIL_LABELS[index] ?? `Layout ${index + 1}`;
};

export const LAYOUT_THUMBNAILS: { id: LayoutThumbnailId }[] = THUMBS.map((_, i) => ({
  id: `thumb-${i + 1}` as LayoutThumbnailId,
}));

export const LayoutThumbnail = ({
  id,
  selected,
  onClick,
  className,
}: {
  id: LayoutThumbnailId;
  selected: boolean;
  onClick: () => void;
  className?: string;
}) => {
  const uid = useId().replace(/:/g, "");
  const filterId = `${uid}-f`;
  const clipId = `${uid}-c`;
  const index = Number(id.replace("thumb-", "")) - 1;
  const Thumb = THUMBS[index] ?? Thumb1;

  const label = layoutThumbnailLabel(id);

  return (
    <Tooltip>
      <Tooltip.Trigger
        onClick={onClick}
        aria-label={label}
        aria-pressed={selected}
        className={cn(
          "tw-w-full tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0",
          className,
        )}
      >
        <svg
          width="139"
          height="83"
          viewBox="0 0 139 83"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="tw-block tw-h-auto tw-w-full"
          aria-hidden="true"
        >
          <Thumb selected={selected} filterId={filterId} clipId={clipId} />
        </svg>
      </Tooltip.Trigger>
      {/* Above the dialog, which is itself portalled. */}
      <Tooltip.Content className="tw-z-[10001]">
        <Tooltip.Text>{label}</Tooltip.Text>
      </Tooltip.Content>
    </Tooltip>
  );
};
